/**
 * Chats API
 *
 * Endpoints for chat operations
 */

import { z } from "zod";

import type { DbEntity } from "../../../client";
import { DbEnum } from "../../../generated/prisma-enums";
import { ChatIdOrNanoIdSchema } from "../../../shared/chat-lookup";
import {
  ChatAssignedToFilter,
  ChatFilterType,
  ChatStatusFilter
} from "../../../shared/domain/chat/chat.types";
import { chatMessageSchema } from "../../../shared/domain/chat/chat-message.schema";
import { ApiSuccessSchema } from "../../common/response.schema";
import {
  CHAT_METADATA_KEY_REGEX,
  CHAT_METADATA_MAX_ENTRIES,
  CHAT_METADATA_MAX_KEY_LENGTH,
  CHAT_METADATA_MAX_VALUE_LENGTH
} from "../../public/v1/schemas/chat.schemas";
import {
  ChatByIdRecordSchema,
  ChatListRecordSchema,
  ChatSearchRecordSchema,
  ChatWithMembersRecordSchema
} from "./schemas/chat-record.schemas";

/**
 * `settings.embedData` as a WRITE DOOR accepts it.
 *
 * ## Why this field alone is bounded, out of everything `Chat.settings` holds
 *
 * `embedData` is the one key of `Chat.settings` that ends up INSIDE an agent's
 * SYSTEM prompt: `chat.embedData` (`apps/backend/src/chats/classes/chat.ts`)
 * reads it straight off this column and `chatMetadataBlocks` renders it as the
 * *Additional Data* block. Text in a system prompt outranks the turn, so a
 * newline followed by a fresh imperative reads to the model as a platform
 * instruction. Every other key here steers product behaviour and reaches no
 * prompt.
 *
 * ## The numbers are IMPORTED, not chosen
 *
 * `CHAT_METADATA_*` are the v1 chat door's constants, and
 * `chat-metadata-bounds-match-the-prompt-fence.spec.ts` already pins all four
 * against `prompt-metadata.ts`. Reusing them means this door inherits that pin
 * for free; re-declaring 20/64/512 here would be a third copy with no gate,
 * which is the drift that spec exists to refuse. Equal-not-tighter is the whole
 * point: looser accepts text the fence then silently truncates, tighter refuses
 * text the fence renders happily.
 *
 * ## 🔴 IT DOES NOT REFUSE THE RESERVED IDENTITY KEYS, AND THAT IS NOT AN OVERSIGHT
 *
 * `SendChatMessageMetadataSchema` refuses `external_user_id`, `customer_id` and
 * their five siblings, because on that route the server owns the visitor's
 * identity — it is the session token's HMAC-verified `externalUserId` claim, and
 * a caller-typed twin would land in the same prompt block with nothing to say
 * which is authoritative.
 *
 * Here the opposite holds. This route REPLACES `Chat.settings` wholesale, and
 * the console widget's own `POST /api/chatrooms` path writes exactly those seven
 * names into `settings.embedData` (`chatApi.ts` derives them from
 * `EMBED_URL_KEY_MAP`). A write door that refuses to accept back the shape the
 * platform itself stores turns any future read-modify-write into a 400 on
 * precisely the chats that use the feature. The caller here is a first-party
 * operator holding a Clerk session, not an anonymous visitor, so there is no
 * identity to forge past.
 *
 * ⚠️ The KEY REGEX is the one bound that IS tighter than the fence, by the
 * asymmetry `PROMPT_METADATA_KEY_REGEX`'s own docblock grants a write door: it
 * may refuse a key, because the caller is still there to be told, while the
 * compose step may only truncate. Overturn condition: if a caller is ever added
 * that round-trips `settings` read from a chat created through
 * `POST /api/chatrooms` — whose `settings` field bounds nothing at all — a key
 * with a dash in it will 400 here. That is a visible refusal rather than silent
 * damage, and the correct repair is to bound the create door too, never to relax
 * this one.
 */
export const ChatSettingsEmbedDataWriteSchema = z
  .record(
    z.string().max(CHAT_METADATA_MAX_KEY_LENGTH).regex(CHAT_METADATA_KEY_REGEX),
    z.string().max(CHAT_METADATA_MAX_VALUE_LENGTH)
  )
  .refine((value) => Object.keys(value).length <= CHAT_METADATA_MAX_ENTRIES, {
    message: `embedData may carry at most ${CHAT_METADATA_MAX_ENTRIES} entries`
  });

/**
 * The body of `PUT /api/chatrooms/:chatRoomId/settings`.
 *
 * ## LOOSE on purpose, and only on purpose
 *
 * `ChatSettings` carries an `[key: string]: any` index signature and the column
 * is a free-form Json field written by several channels, so a `.strict()` object
 * would refuse settings shapes no schema in this package enumerates. What the
 * loose object DOES buy is the thing `z.custom<ChatSettings>()` did not: a
 * non-object is now a 400. Measured on zod 4.1.13, `z.custom<T>()` with no
 * validator returns `success: true` for `undefined`, `null`, `42`, `"x"` and
 * `[]` alike — so the previous spelling put a bare scalar into a Json column
 * through a route whose contract said `ChatSettings`.
 *
 * ⚠️ IT STILL REPLACES `settings` WHOLESALE, which is what `PUT` means and is
 * left alone deliberately: no caller in this repository invokes this route
 * (`api.chats.useUpdateSettings` has zero call sites), so a merge semantics
 * change would be a guess at a consumer that does not exist. The blast radius of
 * the replacement is bounded by reachability rather than by the schema — the
 * route admits a Clerk session and nothing else, and both statements of
 * `PrismaChatRepository.updateSettings` filter on `{ id, organizationId }`.
 */
export const ChatSettingsWriteSchema = z.looseObject({
  embedData: ChatSettingsEmbedDataWriteSchema.optional()
});

/**
 * `settings` as `POST /api/chatrooms` accepts it — an ALLOWLIST, where its
 * `PUT` sibling above is a loose object.
 *
 * ## The two doors differ because their CALLERS differ
 *
 * `PUT /api/chatrooms/:chatRoomId/settings` admits a Clerk session and nothing
 * else. This route carries `@AllowForEmbeddedChats()`, so `EmbedAuthGuard`
 * admits a browser holding only a deployment's PUBLIC `x-agent-id` header:
 * `@CurrentUserId()` resolves to `undefined` and the organization is derived
 * from the deployment row. A visitor who has never authenticated writes this
 * body.
 *
 * ## Every key of `Chat.settings` the backend reads was reachable from there
 *
 * The controller spread the caller's `settings` whole and
 * `CreateChatroomUseCase` persists it verbatim
 * (`settings: (input.settings as ChatData["settings"]) ?? null`), so all five
 * readers took visitor-authored values:
 *
 * | key | reader | what it steers |
 * |---|---|---|
 * | `embedData` | `chat.embedData` | the *Additional Data* system-prompt block |
 * | `additionalTools.whatsapp` | `Chat.getCurrentAgentFunctions`, `chat-assistant.service.ts` | the model-facing TOOL CONTRACT — name, description, parameter schema — and the executable dispatch list |
 * | `deploymentType` | `chat.deploymentType` | channel classification, and through it the prompt's format rules |
 * | `skipImagesInHistory` | `Chat.buildWorkingMessages` | whether image content enters the window |
 * | `addContext` | `chat.addContext` | nothing — the getter has no caller |
 *
 * 🔴 `additionalTools` is the one that outruns the prompt fence rather than
 * passing through it. `renderPromptMetadataBlock` bounds and quotes everything
 * that reaches the *Additional Data* block; a tool NAME and DESCRIPTION are not
 * prompt metadata and meet none of it — `createFunctionsFromWhatsapp` renders
 * `whatsapp_${tool.name}` and the caller's `description` straight into the
 * function list the model is shown.
 *
 * ## Why an allowlist and not a denylist of those four
 *
 * A denylist is correct only until someone adds the sixth reader, and nothing
 * reds when they do — the same failure
 * `PublicEmbedSettings.schema.ts` was built to replace on the READ side of this
 * same widget. `z.strictObject` inverts it: a key that is not named here is a
 * 400, so a new `ChatSettings` field cannot join the anonymous write surface by
 * being declared.
 *
 * ## The blast radius is measured, not assumed
 *
 * No caller in this repository sends `settings` to this route. There are two:
 * `createChatRoom` (`apps/frontend/src/common/services/chat/chatApi.ts`),
 * which is the widget and builds its body from
 * `content`/`id`/`chatRoomId`/`knowledgeIds`/`images`/`generateTopic`/`embedData`;
 * and `TestChatView.tsx`, which sends four fields. Neither names `settings`.
 * An org API key cannot reach this route at all — `@PublicAPI()` with no scope
 * argument sets no `PUBLIC_API_SCOPE_KEY`, and `PublicApiKeyGuard.checkRouteScope`
 * reads that absence as `insufficientScope("(undeclared)")` for every key kind.
 */
export const CreateChatRoomSettingsWriteSchema = z.strictObject({
  embedData: ChatSettingsEmbedDataWriteSchema.optional()
});

/**
 * Chats API Schemas
 */
export const ZChats = {
  /**
   * List inbox chat rooms (real integration conversations).
   * Requires INBOX feature access. Type is hardcoded server-side.
   */
  ListInbox: {
    method: "GET" as const,
    path: "/api/chatrooms/inbox",
    Params: z.object({
      filter: z.nativeEnum(ChatFilterType).optional(),
      page: z.coerce.number().int().min(0).optional(),
      deploymentId: z.string().optional(),
      status: z.nativeEnum(ChatStatusFilter).nullable().optional(),
      ticketStatus: z.nativeEnum(DbEnum.ChatTicketStatus).nullable().optional(),
      assignedTo: z.nativeEnum(ChatAssignedToFilter).nullable().optional(),
      responseHandling: z.nativeEnum(DbEnum.ChatResponseHandling).nullable().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        data: z.array(ChatListRecordSchema),
        hasNextPage: z.boolean(),
        page: z.number().int()
      })
    )
  },

  /**
   * List playground chat rooms (test conversations, no real integration).
   * Requires PLAYGROUND feature access. Type is hardcoded server-side.
   */
  ListPlayground: {
    method: "GET" as const,
    path: "/api/chatrooms/playground",
    Params: z.object({
      filter: z.nativeEnum(ChatFilterType).optional(),
      page: z.coerce.number().int().min(0).optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        data: z.array(ChatListRecordSchema),
        hasNextPage: z.boolean(),
        page: z.number().int()
      })
    )
  },

  /**
   * Export chat comments as CSV
   * Returns binary blob (CSV file)
   */
  ExportComments: {
    method: "GET" as const,
    path: "/api/chatrooms/export-comments",
    Params: z.object({
      deploymentId: z.string().optional()
    }),
    responseType: "blob" as const
  },

  /**
   * Get assigned users for a chat
   * Returns the list of assigned user IDs and response handling mode
   */
  GetAssignedUsers: {
    method: "GET" as const,
    path: "/api/chatrooms/:chatRoomId/assignedUsers",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Response: ApiSuccessSchema(
      z.object({
        assignedUsers: z.array(z.object({ id: z.string() })),
        responseHandling: z.string()
      })
    )
  },

  /**
   * Get a single chat room by ID or nanoId.
   * Returns full chat data including members, deployment session, and last message.
   *
   * 🚨 THE ONE `ZChats` ROUTE WHOSE PARAM IS NOT UUID-ONLY, AND THE SCHEMA SAYS
   * SO BECAUSE THE HANDLER DOES. This one resolves through
   * `FindChatByLookupPort` — `prisma-chat.repository.ts`'s `getChatById` is its
   * only call site — so it answers a `Chat.nanoId` shortcode as readily as the
   * uuid. `resolveChatLookup`'s header puts the obligation here: *"the caller is
   * responsible for validating the input shape (UUID or nanoId) upstream of this
   * helper"*.
   *
   * Uuid-only here would make a consumer that parses `PathVars` refuse an id
   * this route answers, and nothing would go red — the handler keeps working, so
   * only a schema reader is misled.
   *
   * Every other `ZChats` route reads `Chat.id` directly and stays
   * `z.string().uuid()`. `get-by-id-accepts-the-lookup-it-resolves.test.ts` pins
   * both halves, including that the union does not spread.
   */
  GetById: {
    method: "GET" as const,
    path: "/api/chatrooms/:chatRoomId",
    PathVars: z.object({ chatRoomId: ChatIdOrNanoIdSchema }),
    Params: z.object({ type: z.enum(["inbox", "playground"]) }),
    Response: ApiSuccessSchema(ChatByIdRecordSchema)
  },

  /**
   * Get all members (users + virtual members) in a chat room.
   * Used to hydrate chat list items on realtime updates.
   */
  GetMembers: {
    method: "GET" as const,
    path: "/api/chatrooms/:chatRoomId/members",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Response: ApiSuccessSchema(ChatWithMembersRecordSchema)
  },

  /**
   * Set (replace) assigned users for a chat
   * Replaces the full assignment list — pass empty array to clear all
   */
  SetAssignedUsers: {
    method: "PUT" as const,
    path: "/api/chatrooms/:chatRoomId/assigned-users",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Body: z.object({ userIds: z.array(z.string()).max(50) }),
    Response: ApiSuccessSchema(
      z.object({
        assignedUsers: z.array(z.object({ id: z.string() }))
      })
    )
  },

  /**
   * Replace chat settings (JSON field) — revalidates cache, emits WebSocket event.
   *
   * See {@link ChatSettingsWriteSchema} for why the body is loose everywhere
   * except `embedData`, which is the one key that reaches a system prompt.
   */
  UpdateSettings: {
    method: "PUT" as const,
    path: "/api/chatrooms/:chatRoomId/settings",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Body: ChatSettingsWriteSchema,
    Response: ApiSuccessSchema(z.object({ count: z.number() }))
  },

  /**
   * Update chat status, ticket status, and response handling.
   * Fields are optional — frontend may send partial updates (Prisma ignores undefined).
   */
  UpdateStatuses: {
    method: "PUT" as const,
    path: "/api/chatrooms/:chatRoomId/statuses",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Body: z.object({
      status: z.nativeEnum(DbEnum.ChatStatus).optional(),
      ticketStatus: z.nativeEnum(DbEnum.ChatTicketStatus).optional(),
      responseHandling: z.nativeEnum(DbEnum.ChatResponseHandling).optional()
    }),
    Response: ApiSuccessSchema(z.custom<DbEntity.Chat>())
  },

  /**
   * Update chat topic — emits WebSocket event
   */
  UpdateTopic: {
    method: "PUT" as const,
    path: "/api/chatrooms/:chatRoomId/topic",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Body: z.object({ topic: z.string() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.Chat>())
  },

  /**
   * Add an internal comment to a chat room
   */
  AddComment: {
    method: "POST" as const,
    path: "/api/chatrooms/:chatRoomId/comments",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Body: z.object({ content: z.string() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.Chat>())
  },

  /**
   * Batch soft-delete chat rooms (sets status to DELETED).
   * Partial-success safe — returns succeeded and failed IDs.
   */
  BatchDelete: {
    method: "DELETE" as const,
    path: "/api/chatrooms/batch",
    Body: z.object({ chatRoomIds: z.array(z.string()).min(1).max(100) }),
    Response: ApiSuccessSchema(
      z.object({
        succeeded: z.array(z.string()),
        failed: z.array(z.object({ chatRoomId: z.string(), error: z.string() }))
      })
    )
  },

  /**
   * Batch update status, ticketStatus, and responseHandling across multiple chats.
   * Partial-success safe — returns succeeded and failed IDs.
   */
  BatchUpdateStatuses: {
    method: "POST" as const,
    path: "/api/chatrooms/batch/statuses",
    Body: z.object({
      chatRoomIds: z.array(z.string()).min(1).max(100),
      status: z.nativeEnum(DbEnum.ChatStatus).optional(),
      ticketStatus: z.nativeEnum(DbEnum.ChatTicketStatus).optional(),
      responseHandling: z.nativeEnum(DbEnum.ChatResponseHandling).optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        succeeded: z.array(z.string()),
        failed: z.array(z.object({ chatRoomId: z.string(), error: z.string() }))
      })
    )
  },

  /**
   * Soft-delete a chat room (sets status to DELETED)
   */
  Delete: {
    method: "DELETE" as const,
    path: "/api/chatrooms/:chatRoomId",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.Chat>())
  },

  /**
   * Delete all messages in a chat room (irreversible)
   */
  CleanUp: {
    method: "DELETE" as const,
    path: "/api/chatrooms/:chatRoomId/messages",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Response: ApiSuccessSchema(z.undefined())
  },

  /**
   * Get all files attached to a message
   */
  GetMessageFiles: {
    method: "GET" as const,
    path: "/api/chatrooms/message/:messageId/files",
    PathVars: z.object({ messageId: z.string().uuid() }),
    Response: ApiSuccessSchema(z.array(z.custom<DbEntity.File>()))
  },

  /**
   * Mark all messages in a chat room as read for the current user
   */
  MarkAsRead: {
    method: "PUT" as const,
    path: "/api/chatrooms/:chatRoomId/markAsRead",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Response: ApiSuccessSchema(
      z.custom<(DbEntity.Chat & { members: DbEntity.ChatUserMember[] }) | null>()
    )
  },

  /**
   * Save user satisfaction score for an AI response message — the thumbs
   * control (widget, embed, playground, Teams). Always framework THUMBS.
   * Score range: -1 (thumbs down), 0 (neutral/removed), 1 (thumbs up). A
   * genuine 1-5 rating is a different instrument — see `rateMessage` on
   * the public API.
   */
  SaveSatisfactionScore: {
    method: "POST" as const,
    path: "/api/chatrooms/:messageId/saveSatisfactionScore",
    PathVars: z.object({ messageId: z.string().uuid() }),
    Body: z.object({
      score: z.number().min(-1).max(1),
      comment: z.string().optional()
    }),
    Response: ApiSuccessSchema(z.custom<DbEntity.SatisfactionScore>())
  },

  /**
   * Stop all in-progress AI message processing in a chat room
   */
  StopProcessing: {
    method: "PUT" as const,
    path: "/api/chatrooms/:chatRoomId/stop",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Response: ApiSuccessSchema(z.object({ count: z.number(), workflowsStopped: z.number() }))
  },

  /**
   * Regenerate the last AI message in a chat room
   */
  RegenerateMessage: {
    method: "POST" as const,
    path: "/api/chatrooms/:chatRoomId/regenerate",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Response: ApiSuccessSchema(z.unknown())
  },

  /**
   * Add a message to a chat as a support agent/representative (internal only, no @PublicAPI)
   */
  AddMessageAsAgent: {
    method: "POST" as const,
    path: "/api/chatrooms/:chatRoomId/addMessageAsAgent",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Body: z.object({ content: z.string() }),
    Response: ApiSuccessSchema(z.undefined())
  },

  /**
   * Delete a draft message that was pending validation (internal only, no @PublicAPI)
   */
  DeleteMessageToValidate: {
    method: "DELETE" as const,
    path: "/api/chatrooms/:chatRoomId/deleteMessageToValidate/:messageId",
    PathVars: z.object({ chatRoomId: z.string().uuid(), messageId: z.string().uuid() }),
    Response: ApiSuccessSchema(z.undefined())
  },

  /**
   * Full-text search across chatrooms (topic, message content, phone numbers).
   * Returns chats matching the query with optional snippet highlighting.
   * Permissions are filtered server-side based on the caller's JWT role.
   */
  Search: {
    method: "GET" as const,
    path: "/api/chatrooms/search",
    Params: z.object({
      query: z.string(),
      deploymentId: z.string().optional()
    }),
    Response: ApiSuccessSchema(z.array(ChatSearchRecordSchema))
  },

  /**
   * Get messages in a chatroom with optional cursor-based pagination.
   * Supports embedded chat access (@AllowForEmbeddedChats).
   */
  GetMessages: {
    method: "GET" as const,
    path: "/api/chatrooms/:chatRoomId/messages",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Params: z.object({
      limit: z.coerce.number().int().optional(),
      sortBy: z.enum(["createdAt"]).optional(),
      order: z.enum(["asc", "desc"]).optional(),
      before: z.string().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        messages: z.array(chatMessageSchema),
        hasMore: z.boolean(),
        count: z.number()
      })
    )
  },

  /**
   * Send a WhatsApp content template to the chat contact via Twilio.
   * Polls until the message reaches a finalized status.
   */
  SendWhatsappTemplate: {
    method: "POST" as const,
    path: "/api/chatrooms/:chatRoomId/sendWhatsappTemplate",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    Body: z.object({
      template: z.custom<{ id: string; language: string; types: Record<string, unknown> }>(),
      templateData: z.record(z.string(), z.string())
    }),
    Response: ApiSuccessSchema(
      z.object({
        success: z.boolean(),
        messageId: z.string(),
        status: z.string().optional()
      })
    )
  },

  /**
   * Validate a draft human-agent message and send it through the assistant.
   * Also connects the user to the chat and updates pending message state.
   *
   * `chatRoomId` is a real parent, not decoration: the server answers 404 unless
   * the message is a `WAITING_VALIDATION` draft in THAT chat, in the caller's own
   * organisation. An absent id, another organisation's id and an id under a
   * different chat are one answer, deliberately (NEX-3872).
   *
   * Carried no `agentId` query parameter since NEX-3872. It only ever fed a
   * create-on-absent branch that no longer exists, so it was inert.
   */
  ValidateAndSendMessage: {
    method: "POST" as const,
    path: "/api/chatrooms/:chatRoomId/validateAndSendMessage/:messageId",
    PathVars: z.object({ chatRoomId: z.string().uuid(), messageId: z.string().uuid() }),
    Body: z.object({ content: z.string() }),
    Response: ApiSuccessSchema(z.undefined())
  },

  /**
   * Generate and download a file (PDF, DOCX, etc.) from a message's text content.
   * Returns binary blob.
   *
   * Reachable without a session but NOT without a credential
   * (`@PublicAPI() @AllowForEmbeddedChats()`): an embed widget authenticates with
   * its deployment's `x-agent-id` header, the console and Inbox with a Clerk JWT.
   * The message is read within the caller's organization only, so `messageId`
   * alone opens nothing.
   */
  GenerateFile: {
    method: "POST" as const,
    path: "/api/chatrooms/messages/generate-file",
    Body: z.object({
      messageId: z.string().uuid().optional(),
      fileFormat: z.string()
    }),
    responseType: "blob" as const
  },

  /**
   * Create a new chat room (optionally with initial message).
   * Supports embedded chats (@AllowForEmbeddedChats). JSON only — attachments
   * are uploaded separately and referenced here by id or URL.
   *
   * The handler is `@PublicAPI()` with NO scope argument, which is not the same
   * thing as being reachable with an org API key: `PublicApiKeyGuard.checkRouteScope`
   * refuses an undeclared scope for every key kind, so the caller set is a Clerk
   * session or an embed `x-agent-id` header. The second of those holds no
   * credential, which is why both metadata fields below are bounded — see
   * {@link CreateChatRoomSettingsWriteSchema}.
   */
  CreateChatRoom: {
    method: "POST" as const,
    path: "/api/chatrooms",
    Body: z.object({
      content: z.string().optional(),
      id: z.string().uuid().optional(),
      /**
       * The id the chat is created under, supplied by the caller.
       *
       * A UUID, because `Chat.id` is `@db.Uuid` and every producer already sends
       * one: the widget posts back the id `GetDeploymentChatroomUseCase`
       * reserved (`uuid()`), and the playground and the test chat mint theirs
       * with `crypto.randomUUID()`. Bounding it here is what keeps an arbitrary
       * string from reaching Prisma, where the driver answers a malformed id
       * with a message of its own.
       *
       * Bounding the FORMAT is not bounding the VALUE: any id at all can still
       * arrive, including a chat that already exists. `OpenChatRoomUseCase`
       * answers that one, before anything is written.
       */
      chatRoomId: z.string().uuid().optional(),
      knowledgeIds: z.array(z.string()).optional(),
      images: z.array(z.string()).optional(),
      settings: CreateChatRoomSettingsWriteSchema.optional(),
      /**
       * Customer identity data for embed sessions.
       *
       * The controller MERGES this over `settings.embedData` and stores the
       * result, so it is the same sink and takes the same bound. It was already
       * narrower than its sibling — `z.record(z.string(), z.string())` refuses a
       * nested value — and that is the only half of the fence it had: key shape,
       * key length, value length and entry count were all unbounded, on the one
       * of the two fields the widget actually populates.
       */
      embedData: ChatSettingsEmbedDataWriteSchema.optional()
    }),
    Response: ApiSuccessSchema(z.custom<{ data: Record<string, unknown>; messages: unknown[] }>())
  },

  /**
   * Send a message to an existing (or auto-created) chat room.
   * Supports embedded chats (@AllowForEmbeddedChats). Returns 202.
   * @PublicAPI — accepts org-level API key in addition to JWT
   */
  SendMessage: {
    method: "POST" as const,
    path: "/api/chatrooms/:chatRoomId/messages",
    PathVars: z.object({ chatRoomId: z.string().uuid() }),
    // No `contentType`. A message's stored type is decided by the WRITE PATH on
    // the server, never named by the caller: this endpoint writes a participant
    // message (`AddMessageAsUserUseCase` → `HUMAN`), and writing as the agent is
    // a different use case (`AddMessageAsAgentUseCase` → `AI`).
    //
    // The field used to be here, was accepted, and was discarded — the backend
    // has never honoured it (NEX-3678). Honouring it instead of removing it was
    // the worse option, because the stored type steers counters a caller must
    // not reach: `PrismaSessionStatsRepository.getSessionStats` counts only
    // `type: "HUMAN"`, so a caller able to name its own type could keep its
    // messages out of a deployment's statistics, and `mapMessageRole` derives
    // sender attribution from the same column.
    Body: z.object({
      content: z.string(),
      id: z.string().uuid().optional(),
      knowledgeIds: z.array(z.string()).optional(),
      images: z.array(z.string()).optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        chatRoomId: z.string(),
        data: z.object({
          id: z.string(),
          organizationId: z.string().nullable(),
          status: z.string(),
          topic: z.string().nullable(),
          createdAt: z.coerce.date(),
          updatedAt: z.string()
        }),
        messageData: z.unknown().nullable()
      })
    )
  },

  /**
   * Reset the code interpreter sandbox for a chat.
   *
   * Destroys the current sandbox and clears the session record.
   * The next message will spin up a fresh sandbox from the current snapshot.
   * Conversation history is preserved — only the sandbox environment is reset.
   */
  ResetCodeInterpreterSession: {
    method: "DELETE" as const,
    path: "/api/chats/:chatId/code-interpreter/session",
    PathVars: z.object({ chatId: z.string() }),
    Response: ApiSuccessSchema(z.object({ reset: z.boolean() }))
  },

  /**
   * GET /api/chats/:chatId/code-interpreter/workspace-mounts
   *
   * Returns the workspace slugs that were physically mounted on this chat's
   * sandbox at creation time. Per-turn paths intersect this against the
   * agent's current DB assignments — mid-session admin changes don't take
   * effect until sandbox recycle. UI uses this so the workspaces indicator
   * shows the truth (what the agent actually has access to) rather than the
   * fresh DB intent (which can diverge after admin edits).
   *
   * `mountedSlugs` is `null` when no CodeInterpreterSession row exists yet
   * (brand-new chat, or first message still mid-processing). Callers treat
   * that as "no durable snapshot — trust the agent's current assignments."
   * An empty array means the session exists with zero mounts (env
   * unconfigured or agent had no assignments at boot).
   */
  GetCodeInterpreterWorkspaceMounts: {
    method: "GET" as const,
    path: "/api/chats/:chatId/code-interpreter/workspace-mounts",
    PathVars: z.object({ chatId: z.string() }),
    Response: ApiSuccessSchema(
      z.object({
        mountedSlugs: z.array(z.string()).nullable()
      })
    )
  }
} as const;

/**
 * Inferred TypeScript types
 */
import type { InferSchemas } from "../../schema-inference";
export type TApiChats = InferSchemas<typeof ZChats>;
